import './CheckboxComponent.scss';
import { useState, useEffect, useCallback, FC, useRef } from 'react';
import { CheckBoxState } from './model';
import { FilterLevel, FilterType, FilterVM } from '../../../common/view-model';
import { useSelector } from 'react-redux';
import { FilterActions } from '../../../redux/FilterActions';
import { RootState } from '../../../redux/AppReducer';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { v4 as uuidv4 } from 'uuid';
import { publicProductDiscoveryActions } from '../../../common/constants';
import noChecked from '../../../assets/images/no-check.svg';
import hoverChecked from '../../../assets/images/check-hover.svg';
import checkedIcon from '../../../assets/images/check.svg';
import indeterminate from '../../../assets/images/indeternimate.svg';
import { useAppDispatch } from '../../../redux/store';
import LabelForms from '../../forms/GenericBaseForms/LabelForms/LabelForms';

export interface ICheckboxComponentProps {
	checked?: boolean;
	label?: string;
	children?: any[];
	parentIndex?: string;
	index?: number;
	style?: string;
	level?: FilterLevel;
	type?: FilterType;
	parentId?: string;
	parentLength: number;
	mainMenuId?: string;
	checkBoxGroupId?: string;
	categoryName?: string;
	carrotIconRef?: string;
	isSuperCategory?: boolean;
	breadcrumText?: string;
	checkBoxDOMRef?: string;
	isLocked?: boolean;
	setLockModalContent?: () => void;
	registerUserClickEvent?: (event: string) => void;
	pendingFilters?: FilterVM[];
	setPendingFilters?: React.Dispatch<React.SetStateAction<FilterVM[]>>;
	isNewFilter?: boolean;
	toolTipText?: React.ReactElement;
}
export const CheckboxComponent: FC<ICheckboxComponentProps> = ({
	checked,
	label,
	children,
	parentIndex,
	index,
	style,
	level,
	type,
	parentId,
	parentLength,
	mainMenuId,
	checkBoxGroupId,
	categoryName,
	carrotIconRef,
	isSuperCategory,
	breadcrumText,
	checkBoxDOMRef = `checklevel${uuidv4()}`,
	isLocked,
	setLockModalContent,
	registerUserClickEvent,
	pendingFilters,
	setPendingFilters,
	isNewFilter,
	toolTipText
}) => {
	const dispatch = useAppDispatch();
	const checkBoxContainerRef = useRef<HTMLDivElement>(null);
	const FULL_SIZE_SCREEN_WIDTH = 768;
	const { width } = useWindowSize();
	const isMobile = width < FULL_SIZE_SCREEN_WIDTH;
	const selectedFilters = useSelector(
		(state: RootState) => state.appState.filterState?.selectedFilters
	);
	const [state, setState] = useState({
		...CheckBoxState,
		checkProperties: {
			checked,
			label,
			children,
			parentIndex,
			index,
			style,
			level,
			type,
			parentId,
			parentLength,
			mainMenuId,
			checkBoxGroupId,
			categoryName,
			carrotIconRef,
			isSuperCategory,
			breadcrumText,
			checkBoxDOMRef
		}
	});
	const addFilterStateCallback = useCallback(
		() => dispatch(FilterActions.addFilterState({ parentIndex, state, setState })),
		[dispatch]
	);
	const checkedStore =
		selectedFilters?.some(filter => filter.id === parentIndex && filter.name === label) ?? false;

	const checkedFilters = isMobile
		? pendingFilters?.find(filter => filter.id === parentIndex && filter.name === label)
				?.isChecked ?? checkedStore
		: checkedStore;

	const handleDispatch = (event: any) => {
		console.log('EV: ', event);
		if (isLocked) {
			setLockModalContent?.();
			registerUserClickEvent?.(publicProductDiscoveryActions.CLICK_FILTER_CHECKBOX);
			return;
		}

		const isCheck = event.target.checked;

		const filter: FilterVM = {
			name: label,
			id: parentIndex,
			isChecked: isCheck,
			children,
			level,
			index,
			type,
			state,
			setState,
			parentId,
			parentLength,
			mainMenuId,
			checkBoxGroupId,
			categoryName,
			carrotIconRef,
			isSuperCategory,
			breadcrumText,
			checkBoxDOMRef
		};

		if (isMobile && setPendingFilters) {
			setPendingFilters(prev => {
				const existingIndex = prev.findIndex(f => f.id === filter.id);

				if (existingIndex !== -1) {
					const updated = [...prev];
					updated[existingIndex] = { ...filter };
					return updated;
				}

				return [...prev, { ...filter }];
			});
		} else {
			if (isCheck) {
				dispatch(FilterActions.addFilter(filter));
			} else {
				dispatch(FilterActions.removeFilter(filter, isMobile));
			}
		}
	};
	const validateIndeterminate = useCallback(() => {
		let result = false;
		if (level === FilterLevel.THIRD) {
			const existsParent = selectedFilters?.find(x => x.id === parentIndex) !== undefined;
			const elementByParentCount = selectedFilters?.filter(
				x => x.parentId === parentIndex && x.id !== parentIndex
			).length;
			result = elementByParentCount !== 0 && elementByParentCount! < parentLength && !existsParent;
		}
		return result;
	}, [selectedFilters, parentIndex, parentLength, level]);

	useEffect(() => {
		addFilterStateCallback();
	}, []);

	useEffect(() => {
		if (validateIndeterminate()) {
			const indeterminateElement = document.getElementById(checkBoxDOMRef) as HTMLInputElement;
			indeterminateElement.indeterminate = true;
		} else {
			const indeterminateElement = document.getElementById(checkBoxDOMRef) as HTMLInputElement;
			indeterminateElement.indeterminate = false;
		}
	}, [selectedFilters]);
	useEffect(() => {
		if (!checkBoxContainerRef.current) return;

		const checkBoxContainer = checkBoxContainerRef.current;
		const updateIcon = () => {
			if (validateIndeterminate()) {
				setIconUrl(indeterminate);
			} else if (checkedFilters) {
				setIconUrl(checkedIcon);
			} else {
				setIconUrl(noChecked);
			}
		};

		const handleMouseOver = () => {
			if (!checkBoxContainerRef.current) return;
			if (validateIndeterminate()) {
				setIconUrl(indeterminate);
			} else if (checkedFilters) {
				setIconUrl(checkedIcon);
			} else {
				setIconUrl(hoverChecked);
			}
		};

		const handleMouseLeave = () => {
			if (!checkBoxContainerRef.current) return;
			updateIcon();
		};

		updateIcon();

		checkBoxContainer.addEventListener('mouseover', handleMouseOver);
		checkBoxContainer.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			checkBoxContainer.removeEventListener('mouseover', handleMouseOver);
			checkBoxContainer.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [checkedFilters, validateIndeterminate]);
	useEffect(() => {
		if (!isMobile) return;
		const match = pendingFilters?.find(f => f.id === parentIndex && f.name === label);
		const isChecked =
			match?.isChecked ?? selectedFilters?.some(f => f.id === parentIndex && f.name === label);
		setState(prev => ({
			...prev,
			checkProperties: {
				...prev.checkProperties,
				checked: isChecked ?? false
			}
		}));
	}, [pendingFilters, selectedFilters, isMobile, parentIndex, label]);
	const [iconUrl, setIconUrl] = useState(checkedFilters ? checkedIcon : noChecked);
	return isNewFilter ? (
		<div
			className={`filter-item ${checkedFilters ? 'active' : ''}`}
			ref={checkBoxContainerRef}
			role='button'
			tabIndex={0}
			onClick={() =>
				handleDispatch({
					target: {
						checked: !checkedFilters
					}
				})
			}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					handleDispatch({
						target: {
							checked: !checkedFilters
						}
					});
				}
			}}
		>
			<input
				data-testid='checkbox-component'
				type='checkbox'
				checked={checkedFilters}
				onChange={handleDispatch}
				id={checkBoxDOMRef}
				style={{ display: 'none' }}
			/>

			<span className='dot'></span>

			<div className='label'>{state.checkProperties.label}</div>

			<LabelForms
				text=''
				icon={<i className='ri-information-line' />}
				toolTipText={toolTipText}
				toolTipPosition='right'
			/>
		</div>
	) : (
		<div className='check-container' ref={checkBoxContainerRef}>
			<img
				className='check-icon'
				src={iconUrl}
				alt='check'
				onClick={() =>
					handleDispatch({
						target: { checked: !checkedFilters }
					})
				}
			/>
			<input
				data-testid='checkbox-component'
				type='checkbox'
				value={state.checkProperties.label}
				checked={checkedFilters}
				onChange={handleDispatch}
				id={checkBoxDOMRef}
			/>
			<label
				style={{ color: label === 'Expo West 2026' ? 'rgba(193, 64, 40, 1)' : 'inherit' }}
				className={`check-label ${children ? 'parent-label' : ''}${
					checkedFilters ? ' selected' : ''
				}`}
				htmlFor={checkBoxDOMRef}
			>
				{state.checkProperties.label}
			</label>
		</div>
	);
};
